import { InteractionStatus } from "@azure/msal-browser";
import { Box, Grid } from "@mui/material";
import FormSkeleton from "common/skeleton";
import { useEffect, useState, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import LinksSection from "components/Links";
import DataTransferSection from "components/DataTransfer";
import FileGrid from "components/ObjectList";
import { getLoggedInUser } from "utils/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { ISusData } from "components/SpaceList";
import type { ColumnsType } from "antd/es/table";
import { deleteSpace } from "components/grid/gridApi";
import AddOrEditSpace from "components/AddOrEditSpace";
import { NormalizedSpace } from "utils/normalizeSpaces";
import { IrequestBody } from "components/grid";
import { formSkeleton } from "components/grid/constants";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import ErrorModal from "components/ErrorModal";
import { getSpacesByDomainName } from "components/SpaceList/spaceListApi";
import { CopyOutlined, DeleteOutlined, MoreOutlined, SettingOutlined, EyeOutlined } from "@ant-design/icons";
import { Empty, Table, Tooltip, message, Menu, Dropdown, Tag, Divider } from "antd";
import SpaceActionMenu from "components/grid/SpaceActionMenu";
import SubscribeMenuItem from "components/grid/SubscribeMenuItem";
import ParametersSecretsModal from "components/grid/ParametersSecretsModal";
import { getReadOnlyDzLink } from "components/grid/gridApi";
import { getSagemakerDomainByDisplayName } from "utils/utils";
import { getS3grantAccessToken } from "config/token";
import { deepLinkFolderPrefix } from "components/ObjectList/deepLink";
import "components/grid/style.scss";

interface IProps {
  spaces: NormalizedSpace[] | undefined;
  domainName: string;
  onRefreshSpaces: () => void;
  isSpaceListFetching: boolean;
  onDeleteAccess: (metaDataUsers: string[]) => void;
  // S3 keys from a deep link (?key=&key=). Used to land in the folder and
  // auto-select the objects once the listing loads.
  targetObjectKeys?: string[];
}

interface SpaceRow {
  spaceName: string;
  spaceNameMasked?: string;
  bucketName: string;
  regionName: string;
  projectType: "Data" | "Analytics" | "–";
  profileType: "Store" | "Explore" | "–";
  gxpFlag: "GxP" | "Non-GxP" | "–";
  expiration: string;
  __onClick__?: (space: string, region: string, spacePrefix: string, bucketName: string, spaceDisplayName: string, sus_git_repo: string, sus_project_id: string, sus_project_link: string, sus_project_metadata: string, gxpFlag: string, data_account_id: string, metaDataUsers?: Array<{ user_email: string; access_level: string; }>, fileSystemType?: string) => void;
  metaDataUsers: string;
  sus_git_repo: string;
  sus_project_id: string;
  sus_project_link: string;
  sus_project_metadata: string;
  data_account_id: string;
  file_system_type: string;
}

type MetaDataUser = { user_email: string; access_level: string; };

const isMetaDataUser = (value: unknown): value is MetaDataUser => {
  if (typeof value !== "object" || value === null) return false;

  const candidate = value as Record<string, unknown>;
  return typeof candidate.user_email === "string" && typeof candidate.access_level === "string";
};

const normalizeMetaDataUsers = (value: unknown): MetaDataUser[] => {
  if (!Array.isArray(value)) return [];
  return value.filter(isMetaDataUser);
};

const parseMetaDataUsers = (metaDataUsers?: string): MetaDataUser[] => {
  if (!metaDataUsers) return [];

  try {
    const parsed: unknown = JSON.parse(metaDataUsers);

    if (Array.isArray(parsed)) return normalizeMetaDataUsers(parsed);

    // Handle legacy/double-encoded payloads safely.
    if (typeof parsed === "string") {
      const reparsed: unknown = JSON.parse(parsed);
      return normalizeMetaDataUsers(reparsed);
    }

    return [];
  } catch {
    return [];
  }
};

export default function SpaceObjects(props: IProps) {
  const { spaces, domainName, onRefreshSpaces, isSpaceListFetching, targetObjectKeys = [] } = props;
  const targetKeySignature = targetObjectKeys.join("\n");
  const linkedFolder = deepLinkFolderPrefix(targetObjectKeys);
  const [deepLinkNoticeSignature, setDeepLinkNoticeSignature] = useState("");
  const [requestBody, setRequestBody] = useState<IrequestBody>({
    region: "",
    data_domain_name: "",
    bucket_name: "",
    space_name: "",
    data_account_id: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPrefix, setCurrentPrefix] = useState<string>("");
  // The address bar remains the source of the selection. Keep applying it
  // on every reload of this folder, and stop only after the user leaves it.
  const keysForOpenFolder = currentPrefix === linkedFolder ? targetObjectKeys : [];
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedSpaceGxpFlag, setSelectedSpaceGxpFlag] = useState<string>("");
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string>("");
  const [selectedDataAccountID, setSelectedDataAccountID] = useState<string>("");
  const [selectedProfileType, setSelectedProfileType] = useState<string>("");
  const [selectedSpaceBucketName, setSelectedSpaceBucketName] = useState<string>("");
  const [selectedSpaceRegion, setSelectedSpaceRegion] = useState<string | null>(null);
  const [selectedSpaceDisplayName, setSelectedSpaceDisplayName] = useState<string>("");
  const [selectedSpaceUserPermissions, setSelectedSpaceUserPermissions] = useState<Array<{ user_email: string; access_level: string; }>>([]);
  const [selectedSpaceFileSystemType, setSelectedSpaceFileSystemType] = useState<string>("");
  const [susData, setSusData] = useState<ISusData>({
    gitRepo: "",
    projectId: "",
    projectLink: "",
    susProjectMetadata: "",
  });
  const [selectedSpace, setSelectedSpace] = useState<string | null>(null);
  const [selectedRegions, setSelectedRegions] = useState<string[]>(["all"]);
  const [spaceObjects, setSpaceObjects] = useState<{ data: any } | null>(null);
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);
  const [paramsModalVisible, setParamsModalVisible] = useState(false);
  const [paramsModalSpace, setParamsModalSpace] = useState<{ spaceName: string; regionName: string; bucketName: string; metaDataUsers?: string; file_system_type?: string } | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const msalInstance = useMsal();
  // Signature of the link whose folder has been opened. A new link must reach
  // its own folder before leaving can clear it.
  const arrivedLinkSignatureRef = useRef<string | null>(null);
  const lastInitialLoadRef = useRef<{ spaces: NormalizedSpace[]; domainName: string; signature: string } | null>(null);

  // The deep-link query stays only while that folder is open. Leaving it,
  // including Back to the space root, removes ?key= so the address matches
  // the folder on screen.
  useEffect(() => {
    if (!targetKeySignature) {
      arrivedLinkSignatureRef.current = null;
      return;
    }
    if (currentPrefix === linkedFolder) {
      arrivedLinkSignatureRef.current = targetKeySignature;
      return;
    }
    if (arrivedLinkSignatureRef.current !== targetKeySignature) return;

    arrivedLinkSignatureRef.current = null;
    const next = new URLSearchParams(location.search);
    next.delete("key");
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "" },
      { replace: true },
    );
  }, [currentPrefix, linkedFolder, targetKeySignature, location.pathname, location.search, navigate]);

const authReady =
  msalInstance.inProgress === InteractionStatus.None &&
  msalInstance.accounts.length > 0;
 

  // Show delete modal
  const showDeleteModal = () => setIsDeleteModalVisible(true);

  // Handle domain selection
  const columns: ColumnsType<SpaceRow> = [
    {
      title: "Space Name",
      dataIndex: "spaceName",
      key: "spaceName",
      render: (text, record) => {
        const displayName = record.spaceNameMasked || text;
        return (
          <span className="space-name-wrapper">
            <Tooltip title={displayName}>
              <span
                className="space-name-text"
                onClick={() => {
                  record.__onClick__?.(
                    record.spaceName,
                    record.regionName,
                    "",
                    record.bucketName,
                    displayName,
                    record.sus_git_repo,
                    record?.sus_project_id,
                    record?.sus_project_link,
                    record?.sus_project_metadata,
                    record.gxpFlag,
                    record.data_account_id,
                    parseMetaDataUsers(record.metaDataUsers),
                    record.file_system_type,
                  );
                }}
              >
                {displayName}
              </span>
            </Tooltip>
            <Tooltip title="Copy space name">
              <CopyOutlined
                className="space-name-copy-icon"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(displayName);
                    message.success("Copied space name!");
                  } catch {
                    message.error("Failed to copy space name");
                  }
                }}
              />
            </Tooltip>
          </span>
        );
      },

    },
    {
      title: "Region",
      dataIndex: "regionName",
      key: "regionName",
    },
    {
      title: "Project Type",
      dataIndex: "projectType",
      key: "projectType",
      onFilter: (value, record) => record.projectType === value,
    },
    {
      title: "Profile Type",
      dataIndex: "profileType",
      key: "profileType",
      onFilter: (value, record) => record.profileType === value,
    },
    {
      title: "GxP / Non-GxP",
      dataIndex: "gxpFlag",
      key: "gxpFlag",

      render: (_: any, record: SpaceRow) => (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {record.gxpFlag.toLocaleLowerCase() === "nongxp" ? (
            <Tag color="green">{record.gxpFlag}</Tag>
          ) : (
            <Tag color="blue">{record.gxpFlag}</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Expires On",
      dataIndex: "expiration",
      key: "expiration",
      // Optionally add filters if needed
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: SpaceRow) => {
        const rowKey = `${record.bucketName}-${record.spaceName}`;
        const isOpen = openDropdownKey === rowKey;
        const extraMenuItems = (
          <>
            <Menu.Item
              key="parametersSecrets"
              style={{ color: "#1677ff", fontWeight: 500, fontSize: 15, paddingLeft: 16 }}
              onClick={(e) => {
                e.domEvent.stopPropagation();
                setOpenDropdownKey(null);
                setParamsModalSpace({
                  spaceName: record.spaceName,
                  regionName: record.regionName,
                  bucketName: record.bucketName,
                  metaDataUsers: record.metaDataUsers,
                  file_system_type: record.file_system_type,
                });
                setParamsModalVisible(true);
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <SettingOutlined style={{ fontSize: 16 }} />
                View/Edit Parameters &amp; Secrets
              </span>
            </Menu.Item>
            <Menu.Item
              key="viewMetadata"
              disabled={!record.sus_project_metadata}
              style={{
                color: record.sus_project_metadata ? "#1677ff" : "#bfbfbf",
                fontWeight: 500,
                fontSize: 15,
                paddingLeft: 16,
              }}
              onClick={async (e) => {
                e.domEvent.stopPropagation();
                setOpenDropdownKey(null);
                const key = "BizMetaKey";
                message.open({ key, type: "info", content: "Fetching Metadata Form...", duration: 8 });
                const sagemaker_id = getSagemakerDomainByDisplayName("Common");
                if (record.sus_project_metadata && sagemaker_id) {
                  const readOnlyAssetID = await getReadOnlyDzLink(msalInstance, record.spaceName);
                  if (readOnlyAssetID.success) {
                    const readOnlyLink = `https://${sagemaker_id}.sagemaker.us-east-1.on.aws/browse/assetListing/${readOnlyAssetID.data}/latest/metadata`;
                    message.open({ key, type: "success", content: "Business Metadata form Loaded Successfully", duration: 2 });
                    window.open(readOnlyLink, "_blank", "noopener,noreferrer");
                  } else {
                    message.open({ key, type: "error", content: "Error fetching Business Metadata form", duration: 2 });
                  }
                }
              }}
            >
              <Tooltip
                title={!record.sus_project_metadata ? "Business metadata not available for this asset" : ""}
                placement="left"
              >
                <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <EyeOutlined style={{ fontSize: 16 }} />
                  View Business Metadata
                </span>
              </Tooltip>
            </Menu.Item>
            <SubscribeMenuItem
              record={record}
              domainName={domainName}
              isOpen={isOpen}
              onClose={() => setOpenDropdownKey(null)}
            />
          </>
        );
        return (
          <SpaceActionMenu
            record={record}
            domainName={domainName}
            onRefreshSpaces={onRefreshSpaces}
            trigger={["click"]}
            isOpen={isOpen}
            onOpenChange={(open) => setOpenDropdownKey(open ? rowKey : null)}
            extraMenuItems={extraMenuItems}
            onDeleteClick={(rec) => {
              const body: IrequestBody = {
                region: rec.regionName,
                data_domain_name: domainName,
                bucket_name: rec.bucketName,
                space_name: rec.spaceName,
                data_account_id: rec.data_account_id,
              };
              setRequestBody(body);
              setSelectedSpaceGxpFlag(rec.gxpFlag);
              showDeleteModal();
            }}
          />
        );
      },
    },
  ];

  // Memoized handleViewObjects to avoid unnecessary re-renders and redundant calls
  const handleViewObjects = async (
    spaceName: string,
    region: string,
    spacePrefix: string,
    bucketName: string,
    spaceDisplayName: string,
    sus_git_repo: string,
    sus_project_id: string,
    sus_project_link: string,
    sus_project_metadata: string,
    gxpFlag: string,
    data_account_id: string,
    profileType: string,
    metaDataUsers?: Array<{ user_email: string; access_level: string; }>,
    fileSystemType?: string,
  ) => {
    // Refresh token before setting state
    try {
      setLoading(true);
      const tokenResult = await getS3grantAccessToken(msalInstance, true);
      if (!tokenResult) {
        setLoading(false);
        setSelectedSpace(null);
        message.error("Failed to refresh token. Please sign in again.");
        return;
      }
    } catch (error) {
      setLoading(false);
      setSelectedSpace(null);
      console.error("Failed to refresh token before viewing space:", error);
      message.error("Failed to refresh token. Please sign in again.");
      return;
    }
        
    setCurrentPrefix(spacePrefix);
    setSelectedSpace(spaceName);
    setSelectedSpaceRegion(region);
    setSelectedSpaceDisplayName(spaceDisplayName);
    setSelectedSpaceBucketName(bucketName);
    setSelectedSpaceGxpFlag(gxpFlag);
    setSelectedDataAccountID(data_account_id);
    setSelectedProfileType(profileType);
    setSelectedSpaceUserPermissions(metaDataUsers || []);
    setSelectedSpaceFileSystemType(fileSystemType || "");
    setSusData({
      gitRepo: sus_git_repo,
      projectId: sus_project_id,
      projectLink: sus_project_link,
      susProjectMetadata: sus_project_metadata,
    });
    setLoading(false);
  };
  // Initial load logic extracted for clarity
useEffect(() => {
  let isCancelled = false;

  if (!authReady) return;
  if (!spaces || spaces.length === 0) return;

  const last = lastInitialLoadRef.current;
  const sameSpace = Boolean(last && last.spaces === spaces && last.domainName === domainName);
  lastInitialLoadRef.current = { spaces, domainName, signature: targetKeySignature };
  if (sameSpace && last!.signature === targetKeySignature) return;
  // Clearing ?key= after the user leaves the folder must not move them again.
  if (sameSpace && !targetKeySignature) return;

  const loadObjects = () => {
    if (isCancelled) return;
    
    const space = spaces[0];
    // Land in the folder that contains the first linked object.
    const initialPrefix = deepLinkFolderPrefix(
      targetKeySignature ? targetKeySignature.split("\n") : [],
    );
    handleViewObjects(
      space.spaceName,
      space.regionName,
      initialPrefix,
      space.bucketName,
      space.spaceNameMasked || space.spaceName,
      space.sus_git_repo,
      space?.sus_project_id,
      space?.sus_project_link,
      space?.sus_project_metadata,
      space.gxpFlag,
      space.data_account_id,
      space.profileType,
      parseMetaDataUsers(space.metaDataUsers),
      space.file_system_type || "",
    );
  };

  loadObjects();
  return () => { isCancelled = true; };
}, [authReady, spaces, domainName, targetKeySignature]);

  // Prepare rows for the table
  const rows = spaces?.map((space: NormalizedSpace) => ({
    key: space.spaceName,
    spaceName: space.spaceName,
    spaceNameMasked: space.spaceNameMasked,
    regionName: space.regionName,
    projectType: space.projectType,
    profileType: space.profileType,
    gxpFlag: space.gxpFlag,
    expiration: space.expiration,
    bucketName: space.bucketName,
    data_account_id: space.data_account_id,
    metaDataUsers: space.metaDataUsers || "[]",
    sus_git_repo: space.sus_git_repo,
    sus_project_id: space.sus_project_id,
    sus_project_link: space.sus_project_link,
    file_system_type: space.file_system_type || "",
    sus_project_metadata: space.sus_project_metadata,
    __onClick__: (spaceName: string, region: string, spacePrefix: string, bucketName: string, spaceDisplayName: string, sus_git_repo: string, sus_project_id: string, sus_project_link: string, sus_project_metadata: string, gxpFlag: string, data_account_id: string, metaDataUsers?: Array<{ user_email: string; access_level: string; }>, fileSystemType?: string) => {
      handleViewObjects(
        spaceName,
        region,
        spacePrefix,
        bucketName,
        spaceDisplayName,
        sus_git_repo,
        sus_project_id,
        sus_project_link,
        sus_project_metadata,
        gxpFlag,
        data_account_id,
        space.profileType,
        metaDataUsers,
        fileSystemType,
      );
    },
  }));

  // Handle confirm action in the delete modal
  const handleConfirm = async (hardDelete: boolean, reason: { value: string, label: string }) => {
    setIsDeleteModalVisible(false);
    
    // Check if this is a Non-GxP space
    const isNonGxpSpace = selectedSpaceGxpFlag?.toLowerCase() === "nongxp" || selectedSpaceGxpFlag?.toLowerCase() === "non-gxp";
    const bucketType = isNonGxpSpace ? "nongxp" : "gxp";
    // For Non-GxP spaces, don't include reason and harddelete in the payload
    const payload = isNonGxpSpace 
      ? { ...requestBody, bucket_type: bucketType }
      : { ...requestBody, reason: reason?.value, harddelete: hardDelete, bucket_type: bucketType };
    
    const response = await deleteSpace(msalInstance, payload);
    
    if (response.success) {
      const successMessage = isNonGxpSpace
        ? `Deletion of space ${payload.space_name} has been queued. You will be notified via email once it is completed`
        : `Deletion of space ${payload.space_name} with reason ${reason.label} has been queued. You will be notified via email once it is completed`;
      message.success(successMessage);
    } else {
      // Check if this is a specific violation error that should show in popup
      const errorMessage = response.error || `Error deleting the space ${payload.space_name}.`;
      
      if (errorMessage.includes("Action restricted: Non-GxP Space containing active files cannot be deleted") ||
          errorMessage.includes("Action restricted: GxP Space containing active files or files with delete marker cannot be deleted")) {
        // Show error modal for specific violation errors
        setErrorModalMessage(errorMessage);
        setIsErrorModalVisible(true);
      } else {
        // Show toast for other errors
        const displayMessage = `Error while deleting the Space. ${errorMessage}`;
        message.error(displayMessage);
      }
    }
  };

  // Handle cancel action in the delete modal
  const handleCancel = () => {
    message.info("Deletion cancelled");
    setIsDeleteModalVisible(false);
  };

  const selectedRegionLabel =
    selectedRegions.length === 1
      ? selectedRegions[0]
      : selectedRegions.length === 0 || selectedRegions.includes("all")
        ? "All"
        : selectedRegions.join(",");

  const isSpaceDetailsLoading = loading;
  const hasSpaceSelectionContext = Boolean(selectedSpaceBucketName || selectedSpaceDisplayName);
  const canShowSpaceDetails = Boolean(selectedSpace) && Boolean(spaces?.length) && !isSpaceListFetching;
  const shouldShowPermissionMessage =
    hasSpaceSelectionContext && !isSpaceDetailsLoading && !canShowSpaceDetails && !isSpaceListFetching;

  const renderSpaceDetailsSection = () => {
    if (isSpaceDetailsLoading) {
      return <FormSkeleton layout={formSkeleton} />;
    }

    if (canShowSpaceDetails) {
      return (
        <Box>
          <Divider />
          <FileGrid
            domainName={domainName}
            selectedRegion={selectedRegionLabel}
            spaceName={selectedSpaceBucketName}
            spaceApiName={selectedSpace || undefined}
            spaceDisplayName={selectedSpaceDisplayName}
            currentPrefix={currentPrefix}
            selecteSpaceRegion={selectedSpaceRegion}
            selectedBucket={selectedSpaceBucketName}
            gxpFlag={selectedSpaceGxpFlag}
            DataAccountId={selectedDataAccountID}
            fileSystemType={selectedSpaceFileSystemType}
            targetObjectKeys={keysForOpenFolder}
            deepLinkNoticeShown={deepLinkNoticeSignature === targetKeySignature && targetKeySignature !== ""}
            onDeepLinkNotice={() => setDeepLinkNoticeSignature(targetKeySignature)}
            onFolderClick={(selectedSpace, selected_region, prefix, bucketName, spaceDisplayName) => {
              handleViewObjects(
                selectedSpace,
                selected_region,
                prefix,
                bucketName,
                spaceDisplayName,
                susData?.gitRepo,
                susData?.projectId,
                susData?.projectLink,
                susData?.susProjectMetadata,
                selectedSpaceGxpFlag,
                selectedDataAccountID,
                selectedProfileType,
                selectedSpaceUserPermissions,
                selectedSpaceFileSystemType,
              );
            }}
            onRefreshSpaces={onRefreshSpaces}
            setSelectedSpace={setSelectedSpace}
          />

          <Divider />
          <LinksSection
            spaceDisplayName={selectedSpaceDisplayName}
            spaceName={selectedSpace || ""}
            sageMakerData={susData}
            domainName={domainName}
            region={selectedSpaceRegion || undefined}
            bucketName={selectedSpaceBucketName}
            dataAccountId={selectedDataAccountID}
            profileType={selectedProfileType}
          />

          <Divider />
          {selectedSpaceGxpFlag?.toLowerCase() !== "gxp" && (
            <DataTransferSection
              spaceName={selectedSpaceDisplayName}
              spaceApiName={selectedSpace}
              destinationBucket={selectedSpaceBucketName}
              region={selectedSpaceRegion}
              domainName={domainName}
              msalInstance={msalInstance}
            />
          )}
        </Box>
      );
    }

    if (shouldShowPermissionMessage) {
      return (
        <Grid sx={{ marginTop: "20px" }} container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          <Grid item xs={12}>
            <Empty description="You don't have permission to view this Space" />
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  return (
    <div>
      <Box sx={{ width: "100%", mx: "auto" }}>
        {isSpaceListFetching ? (
          <FormSkeleton layout={formSkeleton} />
        ) : spaces?.length === 0 ? (
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={12}>
              <Empty description="No folders found" />
            </Grid>
          </Grid>
        ) : (

          <div>
            <div style={{ marginBottom: "15px", width: "100%", display: "flex", justifyContent: "start", alignItems: "center" }}>
              {domainName && (
                <Tooltip title="Back" >
                  <button
                    className="back-button"
                    onClick={() => navigate(`/explorer/${domainName}`)}
                  >
                    ← Back
                  </button>
                </Tooltip>
              )}

            </div>
            <Table
              dataSource={rows as SpaceRow[]}
              scroll={{ x: 800 }} // Enables horizontal scroll on small screens
              columns={columns}
              pagination={false}
            />
          </div>
        )}
        <Box sx={{ width: "100%", mx: "auto" }}>
          {renderSpaceDetailsSection()}
        </Box>
      </Box>
      <ConfirmDeleteModal
        visible={isDeleteModalVisible}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        spaceType={selectedSpaceGxpFlag}
        gitRepoLink={susData?.gitRepo}
      />      
      <ErrorModal
        visible={isErrorModalVisible}
        title="Space Deletion Restricted"
        message={errorModalMessage}
        type="restriction"
        onClose={() => setIsErrorModalVisible(false)}
      />
      <ParametersSecretsModal
        visible={paramsModalVisible}
        onClose={() => { setParamsModalVisible(false); setParamsModalSpace(null); }}
        spaceInfo={paramsModalSpace}
        domainName={domainName}
      />
    </div>
  );
}
